// Owns a MediaPipe ObjectDetector (EfficientDet-Lite / COCO) used to spot wild
// mammals looking toward the camera while the phone rides in a lapel pocket in
// the woods. FaceLandmarker is human-only, so this is a separate detector.
//
// IMPORTANT HONESTY NOTE: there is NO real animal-gaze model here. COCO gives us
// only a species label plus a pixel bounding box. Everything about "facing the
// camera" and "eye contact" below is a COARSE geometric heuristic derived from
// the box shape, position, and frame-to-frame motion. It must never be presented
// as a precise measurement of where an animal is actually looking.

import { FilesetResolver, ObjectDetector, type ObjectDetectorResult } from "@mediapipe/tasks-vision";
import { CONFIG } from "../config";
import type { BBox } from "../types";

/**
 * Self-hosted EfficientDet-Lite0 COCO model. The face model path in
 * CONFIG.vision.modelAssetPath is deliberately NOT reused; the integrator
 * vendors this file separately at the path below.
 */
const ANIMAL_MODEL = "/models/efficientdet_lite0.tflite";

/** COCO mammal labels we care about (bird included as an optional extra). */
const MAMMAL_LABELS: readonly string[] = [
  "dog",
  "cat",
  "horse",
  "sheep",
  "cow",
  "bear",
  "elephant",
  "zebra",
  "giraffe",
  "bird",
];

/** Minimum detector confidence for a box to be considered. */
const SCORE_THRESHOLD = 0.35;
/** Max boxes per frame (woodland scenes rarely have more relevant animals). */
const MAX_RESULTS = 10;
/** Exponential smoothing factor for the per-box motion hint (0..1). */
const MOTION_SMOOTHING = 0.5;
/** Normalized centroid delta that maps to motion = 1 (fast movement). */
const MOTION_FULL_SCALE = 0.08;
/** Max normalized centroid distance to treat two frames' boxes as the same animal. */
const MATCH_RADIUS = 0.15;

/** Per-frame best-effort observation for one detected animal. */
export interface AnimalObservation {
  /** Bounding box in normalized coords (0..1), same convention as FaceObservation. */
  bbox: BBox;
  /** Centroid in normalized coords. */
  cx: number;
  cy: number;
  /** COCO label, e.g. "dog", "cat", "horse", "bear", "deer"-not-in-COCO, etc. */
  species: string;
  /** Detector score (0..1). */
  confidence: number;
  /** 0..1 COARSE "oriented toward camera" heuristic (box shape + center bias). */
  headFacing: number;
  /** 0..1 COARSE "looking at me" estimate (headFacing tempered by stillness). */
  eyeContact: number;
  /** 0..1 recent motion hint (smoothed frame-to-frame centroid delta). */
  motion: number;
  /** 0..1 combined lock score; drives locking like human faces. */
  lockScore: number;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

interface TrackState {
  cx: number;
  cy: number;
  motion: number;
}

export class AnimalEngine {
  private detector: ObjectDetector | null = null;
  private lastTimestamp = -1;
  /**
   * Nearest-centroid track memory across frames, used only to derive a smoothed
   * per-box motion hint. Not a full tracker; it is rebuilt greedily each frame.
   */
  private prevTracks: TrackState[] = [];
  public delegate: "GPU" | "CPU" = "CPU";

  /** Load the ObjectDetector (GPU delegate first, CPU fallback), mirroring VisionEngine. */
  async init(): Promise<void> {
    const fileset = await FilesetResolver.forVisionTasks(CONFIG.vision.wasmPath);

    const build = (delegate: "GPU" | "CPU") =>
      ObjectDetector.createFromOptions(fileset, {
        baseOptions: {
          modelAssetPath: ANIMAL_MODEL,
          delegate,
        },
        runningMode: "VIDEO",
        scoreThreshold: SCORE_THRESHOLD,
        maxResults: MAX_RESULTS,
        // Only surface the mammal labels we react to (bird optional).
        categoryAllowlist: [...MAMMAL_LABELS],
      });

    if (CONFIG.vision.preferGpu) {
      try {
        this.detector = await build("GPU");
        this.delegate = "GPU";
        return;
      } catch (err) {
        console.warn("[animals] GPU delegate failed, falling back to CPU:", err);
      }
    }
    this.detector = await build("CPU");
    this.delegate = "CPU";
  }

  /**
   * Detect animals in the current video frame. `timestampMs` must strictly
   * increase between calls; stale timestamps are skipped. Any failure returns [].
   */
  detect(video: HTMLVideoElement, timestampMs: number): AnimalObservation[] {
    if (!this.detector) return [];
    if (timestampMs <= this.lastTimestamp) return [];
    this.lastTimestamp = timestampMs;

    let result: ObjectDetectorResult;
    try {
      result = this.detector.detectForVideo(video, timestampMs);
    } catch (err) {
      console.warn("[animals] detectForVideo failed:", err);
      return [];
    }

    // Prefer the video's intrinsic dims for normalization; ObjectDetector reports
    // bounding boxes in input-image pixels.
    const inW = video.videoWidth || 1;
    const inH = video.videoHeight || 1;

    const detections = result.detections ?? [];
    const observations: AnimalObservation[] = [];
    // Fresh track states for this frame; matched against prevTracks below.
    const nextTracks: TrackState[] = [];
    // Guard so each previous track is consumed at most once (greedy nearest match).
    const usedPrev = new Set<number>();

    for (const det of detections) {
      const box = det.boundingBox;
      if (!box) continue;

      // Top-scoring allowed category for this detection.
      const top = det.categories[0];
      if (!top || !top.categoryName) continue;
      const species = top.categoryName;
      const confidence = clamp01(top.score);

      // Pixel bbox -> normalized 0..1, clamped to the frame.
      const nx = clamp01(box.originX / inW);
      const ny = clamp01(box.originY / inH);
      const nw = clamp01(box.width / inW);
      const nh = clamp01(box.height / inH);
      const bbox: BBox = { x: nx, y: ny, w: nw, h: nh };
      const cx = clamp01(nx + nw / 2);
      const cy = clamp01(ny + nh / 2);

      // --- COARSE head-facing heuristic ------------------------------------
      // Rationale (honestly approximate): an animal facing the camera head-on
      // usually presents a rounder, more square silhouette (aspect near 1),
      // whereas a broadside animal is much wider than tall. We fold in a mild
      // center bias because a mammal turned to look at you is often oriented
      // toward the frame center. This says NOTHING about eye direction.
      const aspect = nh > 1e-5 ? nw / nh : 0; // width / height
      // Squareness: 1 when aspect == 1, decaying as the box gets long/thin.
      const squareness = clamp01(1 - Math.abs(aspect - 1));
      // Center bias: 1 at frame center, 0 at the far edges.
      const centerBias = clamp01(1 - (Math.abs(cx - 0.5) + Math.abs(cy - 0.5)));
      const headFacing = clamp01(0.7 * squareness + 0.3 * centerBias);

      // --- Motion hint (smoothed) ------------------------------------------
      // Match this box to the nearest previous-frame centroid to estimate how
      // much it moved. Greedy nearest neighbour; good enough for a hint.
      let bestIdx = -1;
      let bestDist = MATCH_RADIUS;
      for (let i = 0; i < this.prevTracks.length; i++) {
        if (usedPrev.has(i)) continue;
        const p = this.prevTracks[i];
        if (!p) continue;
        const d = Math.hypot(cx - p.cx, cy - p.cy);
        if (d < bestDist) {
          bestDist = d;
          bestIdx = i;
        }
      }
      let motion: number;
      const prev = bestIdx >= 0 ? this.prevTracks[bestIdx] : undefined;
      if (prev) {
        usedPrev.add(bestIdx);
        const rawMotion = clamp01(bestDist / MOTION_FULL_SCALE);
        // Exponential smoothing against the previous smoothed value.
        motion = clamp01(prev.motion + MOTION_SMOOTHING * (rawMotion - prev.motion));
      } else {
        // No match: treat a newly-appeared box as neutral/still for this frame.
        motion = 0;
      }
      nextTracks.push({ cx, cy, motion });

      // --- COARSE eye-contact heuristic ------------------------------------
      // We CANNOT measure animal gaze here. As a stand-in: a still, front-facing
      // animal is more plausibly locking eyes than a moving, broadside one. So we
      // temper headFacing by stillness (1 - motion). Treat as a weak prior only.
      const stillness = 1 - motion;
      const eyeContact = clamp01(headFacing * (0.4 + 0.6 * stillness));

      // Combined lock score (same shape as the human-face lockScore).
      const lockScore = clamp01(headFacing * (0.5 + 0.5 * eyeContact) * confidence);

      observations.push({
        bbox,
        cx,
        cy,
        species,
        confidence,
        headFacing,
        eyeContact,
        motion,
        lockScore,
      });
    }

    this.prevTracks = nextTracks;
    return observations;
  }

  /** Release the underlying detector. */
  close(): void {
    this.detector?.close();
    this.detector = null;
    this.prevTracks = [];
  }
}
